import React, { useState, useEffect, useContext, memo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Col,
  Row,
  Button,
  Table,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from 'reactstrap';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import PinIcon from 'mdi-react/PinOutlineIcon';
import PinOffIcon from 'mdi-react/PinOffIcon';
import useConfig from '../../../util/useConfig';
import { getBaseDateTime } from '../../../util/date-and-time';
import Api from '../../../util/api';
import useModal from '../../../shared/hooks/useModal';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import { OfferContext } from '../context';
import Pagination from '../../../shared/components/pagination/Pagination';
import skeletonTableLoader from '../../../shared/components/custom/SkeletonLoaders';
import Loading from '../../../shared/components/custom/Loading';
import { partial } from '../../../util/translation/translation';
import Creator from './Creator';
import FormCard from '../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';

const OfferCard = ({ status, onStatusChange }) => {
  const history = useHistory();
  const s = partial('shared');
  const f = partial('OffersFormPage');
  const c = partial('OfferCard');

  const {
    fetchAllOffers,
    filterOffersByCategory,
    deleteOfferById,
    getOfferViews,
    getAllOfferActivations,
  } = Api.offers;
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const { id } = useConfig();
  const [handleModal, visible, deleteID] = useModal();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { categories } = useContext(OfferContext);
  const [categoriesWithFilter, setCategoriesWithFilter] = useState([]);
  const [paginationDetails, setPaginationDetails] = useState({
    first: 1,
    last: 1,
    previous: null,
    next: null,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fromatCategory = (data) => {
    if (data.includes('&')) {
      return data
        .split('&')
        .map((word) => word.trim().toLowerCase())
        .join()
        .replace(',', '_');
    }
    return data.toLowerCase().replace(' ', '_');
  };

  const getAllOffers = async (page, size) => {
    const { data } = await fetchAllOffers(id, page, size, status);

    setLoading(false);
    setOffers(data.offers);
    setFilteredOffers(data.offers);
    setPaginationDetails({
      first: data.first,
      last: data.last,
      previous: data.previous,
      next: data.next,
      total: data.total,
    });
    const offerIds = data.offers.map((offer) => offer.id);
    const [offerViewsResponse, offerActivationsResponse] = await Promise.all([
      getOfferViews({ offerIds }, status),
      getAllOfferActivations({ offerIds }, status),
    ]);
    const mappedOffers = data.offers.map((offer) => ({
      ...offer,
      views:
        offerViewsResponse.data.find((item) => item.offerId === offer.id)
          ?.views || 0,
      activations:
        offerActivationsResponse.data.find((item) => item.offerId === offer.id)
          ?.activations || 0,
    }));
    setOffers(mappedOffers);
    setFilteredOffers(mappedOffers);
  };

  useEffect(() => {
    setSearchQuery('');
    setSelectedCategory('');
    setCurrentPage(1);
    getAllOffers(1, pageSize);
    // getAllOffers/pageSize are redefined every render; depending on them
    // would refetch on every render instead of only when status changes.
    // eslint-disable-next-line
  }, [status]);

  useEffect(() => {
    if (categories.length > 0) {
      setCategoriesWithFilter(categories);
    }
  }, [categories]);

  const pinOffer = async (offerId, isPinned) => {
    setLoading(true);
    await Api.offers.pinOfferById(offerId, { isPinned }, id);
    getAllOffers(currentPage, pageSize);
  };

  const renderTable = (offer) => (
    <tr key={offer.id}>
      <td>
        <Link to={`/offers/edit/${offer.id}`}>{offer.title}</Link>
      </td>
      <td>{c(fromatCategory(offer.category))}</td>
      <td>
        <strong>{s('start')}</strong>:{' '}
        {offer.startDate ? getBaseDateTime(offer.startDate) : '-'} <br />
        <strong>{s('end')}</strong>:{' '}
        {offer.endDate ? getBaseDateTime(offer.endDate) : '-'}
      </td>
      <td>
        <Creator offerId={offer.id} companyId={offer.companyId} />
      </td>
      <td className="text-center">
        {offer.views !== undefined ? (
          <div>{offer.views}</div>
        ) : (
          skeletonTableLoader(1, 1, 50)
        )}
        <small className="text-muted">
          {offer.activations !== undefined
            ? `${offer.activations} ${s('activations')}`
            : skeletonTableLoader(1, 1, 50)}
        </small>
      </td>
      <td>
        <div className="icon-actions">
          <SquareIconButton
            icon={offer?.isPinned ? <PinOffIcon /> : <PinIcon />}
            onClick={() => pinOffer(offer.id, !offer?.isPinned)}
            title={offer?.isPinned ? f('removePin') : f('pinOffer')}
            id={`pin-${offer.id}`}
          />
          <SquareIconButton
            icon={<EditOutlineIcon />}
            onClick={() => history.push(`/offers/edit/${offer.id}`)}
            title={s('edit')}
            id={`edit-${offer.id}`}
          />
          <SquareIconButton
            icon={<ContentCopyIcon />}
            onClick={() => history.push(`/offers/clone/${offer.id}`)}
            title={s('clone')}
            id={`clone-${offer.id}`}
          />
          <SquareIconButton
            icon={<DeleteOutlineIcon />}
            onClick={() => handleModal(offer.id)}
            title={s('delete')}
            tone="danger"
            id={`delete-${offer.id}`}
          />
        </div>
      </td>
    </tr>
  );

  const handleFilterOffers = (category) => {
    setSelectedCategory(category.label);
    filterOffersByCategory(category.id, id, currentPage, pageSize, status).then(
      async (response) => {
        setFilteredOffers(response.data.offers);
        const offerIds = response.data.offers.map((offer) => offer.id);
        const [offerViewsResponse, offerActivationsResponse] =
          await Promise.all([
            getOfferViews({ offerIds }, status),
            getAllOfferActivations({ offerIds }, status),
          ]);
        const mappedOffers = response.data.offers.map((offer) => ({
          ...offer,
          views:
            offerViewsResponse.data.find((item) => item.offerId === offer.id)
              ?.views || 0,
          activations:
            offerActivationsResponse.data.find(
              (item) => item.offerId === offer.id
            )?.activations || 0,
        }));
        setFilteredOffers(mappedOffers);
      }
    );
  };

  const handleSearchOffers = (e) => {
    setSearchQuery(e.target.value);
    setFilteredOffers(
      offers.filter(
        (o) =>
          o.title.toLowerCase().indexOf(e.target.value.toLowerCase()) !== -1
      )
    );
  };

  const deleteOffer = async () => {
    try {
      setLoading(true);
      await deleteOfferById(deleteID, id);
      getAllOffers(currentPage, pageSize);
      toast.success(`${c('offerHasBeenDeleted')}`);
      handleModal();
    } catch (err) {
      setLoading(false);
      toast.error(err);
    }
  };

  const handlePagination = (paginationObject) => {
    if (
      paginationObject.pageSize !== pageSize ||
      paginationObject.currentPage !== currentPage
    ) {
      setCurrentPage(paginationObject.currentPage);
      setPageSize(paginationObject.pageSize);
      getAllOffers(paginationObject.currentPage, paginationObject.pageSize);
    }
  };

  return (
    <Row className="mt-3 mb-4">
      <Loading loading={loading} />
      <DeleteModal
        type="offer"
        visible={visible}
        handleModal={handleModal}
        modalAction={deleteOffer}
      />
      <Col>
        <FormCard
          title={
            <>
              <div className="form-card__switch d-none d-md-inline-flex">
                <button
                  type="button"
                  className={`form-card__switch-btn${
                    status === 'active' ? ' active' : ''
                  }`}
                  onClick={() => onStatusChange('active')}
                >
                  {s('Active')}
                </button>
                <button
                  type="button"
                  className={`form-card__switch-btn${
                    status === 'expired' ? ' active' : ''
                  }`}
                  onClick={() => onStatusChange('expired')}
                >
                  {s('Expired')}
                </button>
              </div>
              <span className="d-md-none">
                {status === 'active' ? s('Active') : s('Expired')}
              </span>
            </>
          }
          actions={
            <>
              <div
                className="d-none d-md-flex align-items-center"
                style={{ gap: 10 }}
              >
                <div className="form noWrap">
                  <div className="form__form-group products-list__search flex">
                    <input
                      value={searchQuery}
                      placeholder={s('search')}
                      onChange={handleSearchOffers}
                    />
                  </div>
                </div>
                <UncontrolledDropdown>
                  <DropdownToggle
                    className="icon icon--right mb-0"
                    outline
                    size="sm"
                  >
                    {selectedCategory === ''
                      ? `${s('filter')}`
                      : selectedCategory}{' '}
                    <ChevronDownIcon />
                  </DropdownToggle>
                  <DropdownMenu className="dropdown__menu">
                    {categoriesWithFilter.map((category) => (
                      <DropdownItem
                        key={category.value}
                        onClick={() => handleFilterOffers(category)}
                      >
                        {c(category.key)}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledDropdown>
                {status === 'active' && (
                  <Link to="/offers/create">
                    <Button size="sm" color="primary" className="mb-0">
                      {f('createOffer')}
                    </Button>
                  </Link>
                )}
              </div>
              <div
                className="d-flex d-md-none align-items-center"
                style={{ gap: 8 }}
              >
                <div className="form noWrap">
                  <div className="form__form-group products-list__search flex">
                    <input
                      value={searchQuery}
                      placeholder={s('search')}
                      onChange={handleSearchOffers}
                    />
                  </div>
                </div>
                <UncontrolledDropdown>
                  <DropdownToggle
                    tag="button"
                    type="button"
                    className="square-icon-btn"
                    caret={false}
                  >
                    <DotsHorizontalIcon />
                  </DropdownToggle>
                  <DropdownMenu right className="dropdown-menu--modern">
                    <DropdownItem
                      active={status === 'active'}
                      onClick={() => onStatusChange('active')}
                    >
                      {s('Active')}
                    </DropdownItem>
                    <DropdownItem
                      active={status === 'expired'}
                      onClick={() => onStatusChange('expired')}
                    >
                      {s('Expired')}
                    </DropdownItem>
                    <DropdownItem divider />
                    {categoriesWithFilter.map((category) => (
                      <DropdownItem
                        key={`mobile-${category.value}`}
                        active={selectedCategory === category.label}
                        onClick={() => handleFilterOffers(category)}
                      >
                        {c(category.key)}
                      </DropdownItem>
                    ))}
                    {status === 'active' && (
                      <>
                        <DropdownItem divider />
                        <DropdownItem tag={Link} to="/offers/create">
                          {f('createOffer')}
                        </DropdownItem>
                      </>
                    )}
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </>
          }
          bodyClassName="form-card__body--flush"
        >
          <Table responsive striped className="table--roomy">
            <thead>
              <tr>
                <th>{s('title')}</th>
                <th>{s('category')}</th>
                <th>{s('date')}</th>
                <th>{c('createdBy')}</th>
                <th>
                  {s('views')} / {s('activations')}
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredOffers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-muted">
                    {c('noRecords')}
                  </td>
                </tr>
              ) : (
                filteredOffers.map((offer) => renderTable(offer))
              )}
            </tbody>
          </Table>
        </FormCard>
        <Pagination
          items={paginationDetails.total || 0}
          rowsPerPage={[10, 20, 30]}
          onChangePage={handlePagination}
        />
      </Col>
    </Row>
  );
};
export default memo(OfferCard);
